import { BusinessSubscriptionModel } from '@pukka/api';
import { TEXT_VARIANTS } from '@pukka/common/constants';
import { DeepReadonly } from '@pukka/common/types';
import { numberUtils, stringUtils } from '@pukka/common/utils';
import { Button, Icon, Modal, Table, Text } from '@pukka/ui-components';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import capitalize from 'lodash/capitalize';
import { twMerge } from 'tailwind-merge';

interface CycleDiscountModalProps {
  readonly open?: boolean;
  readonly data?: readonly DeepReadonly<BusinessSubscriptionModel.RecurringBillingCycle>[];
  readonly onToggleModal?: (open: boolean) => void;
}

function CycleCell({ row, table }: CellContext<BusinessSubscriptionModel.RecurringBillingCycle, unknown>) {
  const length = table.getRowModel().rows.length - 1;

  if (length === 0) {
    return <Text className="text-left">First and Following years</Text>;
  }

  return (
    <Text className="text-center">
      {row.index === length ? 'Following years' : `${capitalize(stringUtils.numberToOrdinal(row.original.cycle ?? 0))} year`}
    </Text>
  );
}

function DiscountCell({ row }: CellContext<BusinessSubscriptionModel.RecurringBillingCycle, unknown>) {
  return <Text className="text-center">{row.original.discount_percentage}%</Text>;
}

function PriceCell({ row }: CellContext<BusinessSubscriptionModel.RecurringBillingCycle, unknown>) {
  return row.original.final_price !== undefined && row.original.original_price !== undefined ? (
    <Text className="text-right">
      Rp {numberUtils.formatCurrency(row.original.final_price)}{' '}
      {row.original.original_price > row.original.final_price ? (
        <Text as="span" className="text-[11px] text-red-500 line-through">
          Rp {numberUtils.formatCurrency(row.original.original_price)}
        </Text>
      ) : null}
    </Text>
  ) : null;
}

const highlighted = [0];

const columns: ColumnDef<BusinessSubscriptionModel.RecurringBillingCycle>[] = [
  {
    header: 'Cycle',
    cell: CycleCell,
    size: 170,
  },
  {
    header: 'Discount',
    cell: DiscountCell,
  },
  {
    header: 'Price',
    cell: PriceCell,
    size: 190,
  },
];

function CycleDiscountModal({ open, data, onToggleModal }: CycleDiscountModalProps) {
  return (
    <Modal unwrap open={open} onOpenChange={onToggleModal}>
      <Modal.Content className="bg-neutral-50 md:w-144">
        <Modal.Title className="flex-row justify-between">
          Outlet Renewal Promo
          <Modal.Trigger asChild>
            <Button variant="nude" size="none" className="rounded-md">
              <Icon icon="close" color="white" />
            </Button>
          </Modal.Trigger>
        </Modal.Title>
        <Modal.Body className="mb-14 mt-4 max-h-128 px-8">
          <Text variant="body-1">
            Maximize your savings and productivity by joining MOKA POS recurring payment now, with a{' '}
            <Text as="strong">special discount</Text> exclusively designed for our valued customers, empowering your business for success.
          </Text>
          {data ? (
            <Table
              rounded
              hideHeader
              bordered
              highlightedRowIndexes={highlighted}
              columns={columns}
              data={data as BusinessSubscriptionModel.RecurringBillingCycle[]}
              className={twMerge(TEXT_VARIANTS['caption-1'].size, 'mt-6')}
            />
          ) : null}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
}

export { CycleDiscountModal };
